import { PublicHeader } from '@/components/PublicHeader';
import { GoogleSignIn } from '@/components/GoogleSignIn';
import { SpecularButton } from '@/components/ui/specular-button/SpecularButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ParticleText } from '@/components/ui/particle-text/ParticleText';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getInviteCode } from '@/lib/householdInvite';

export function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invite = getInviteCode(params.get('invite'));
  const registerUrl = invite ? `/register?invite=${invite}` : '/register';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(identifier.trim(), password);
      navigate(invite ? `/join-household?code=${invite}` : '/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-page flex min-h-svh flex-col bg-background text-foreground">
      <PublicHeader actionTo={registerUrl} actionLabel="Sign up" />

      <main
        id="main-content"
        className="mx-auto grid w-full max-w-7xl flex-1 items-center justify-items-center gap-12 px-5 py-12 sm:px-8 sm:py-16 lg:grid-cols-2 lg:gap-16 lg:px-12"
      >
        <div className="hidden min-h-[520px] w-full flex-col items-center justify-center rounded-[2rem] bg-hero px-6 py-16 lg:flex xl:px-10">
          <ParticleText text="Richtato" />
          <p className="text-center text-lg tracking-tight text-muted-foreground xl:text-xl">
            Personal finance, in focus.
          </p>
        </div>
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="font-serif text-4xl font-normal tracking-tight sm:text-5xl">
              Welcome back.
            </h1>
          </div>

          <GoogleSignIn
            invite={invite}
            disabled={loading}
            onBusyChange={setGoogleLoading}
          />

          <form
            aria-label="Sign in"
            inert={googleLoading}
            aria-busy={loading}
            aria-describedby={error ? 'login-error' : undefined}
            className="space-y-6"
            onSubmit={handleSubmit}
          >
            <div className="space-y-2">
              <Label htmlFor="identifier">Email or username</Label>
              <Input
                id="identifier"
                name="identifier"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                required
                className="h-12 text-base sm:text-sm"
                placeholder="Enter your email or username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="h-12 pr-12 text-base sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-controls="password"
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff aria-hidden="true" className="h-4 w-4" />
                  ) : (
                    <Eye aria-hidden="true" className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <p
                id="login-error"
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm leading-6 text-destructive"
              >
                {error}
              </p>
            )}

            <SpecularButton
              type="submit"
              className="h-12 w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2
                    aria-hidden="true"
                    className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </>
              )}
            </SpecularButton>

            <p className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </p>
          </form>

          <p className="mt-8 border-t border-border pt-6 text-center text-sm leading-6 text-muted-foreground">
            New to Richtato?{' '}
            <Link
              to={registerUrl}
              className="rounded-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Create an account
            </Link>
          </p>
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-6 sm:px-8 lg:px-12">
          <Link
            to="/welcome"
            className="inline-flex items-center gap-2 rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            Back to welcome
          </Link>
          <p className="text-xs text-muted-foreground lg:hidden">
            Personal finance, in focus.
          </p>
        </div>
      </footer>
    </div>
  );
}
